import { ref } from 'vue';
import { defineStore } from 'pinia';
import { StudyModel, type StudyPermissionModel, LoadingStateModel, type StudyImportDiff, type INewStudy, type StudySchemaExport } from '@/models';
import StudyService from '@/services/studyService';
import { DATA_VIEW_FILTER, LocalStorageUtils, routeConstants } from '@/utils';
import { useRouter } from 'vue-router';
import { useDashboardStore } from './dashboard';
import { useEntryListStore } from './entryList';
import { useFormStore } from './forms';
import { usePipelineStore } from './pipeline';


export const useStudyStore = defineStore('study', () => {
  const router = useRouter();
  const dashboardStore = useDashboardStore();
  const entryListStore = useEntryListStore();
  const formStore = useFormStore();
  const pipelineStore = usePipelineStore();
  
  const studies = ref<StudyModel[]>([]);
  const studyLoader = ref(new LoadingStateModel({isLoading: false}));
  const selectedStudy = ref<StudyModel>();
  const selectedStudyId = ref<string>();
  const studyPermissions = ref<StudyPermissionModel>();

  const getStudies = async () => {
    studyLoader.value.startLoading();
    studies.value = await StudyService.getStudies();

    studies.value.sort((a, b) => new Date(b.creation).getTime() - new Date(a.creation).getTime());

    if (studies.value.length === 0) {
      selectedStudy.value = undefined;
      selectedStudyId.value = undefined;
      studyLoader.value.stopLoadingAndFinish();
      dashboardStore.setUserOptionLinks();
      return;
    }

    if (selectedStudyId.value) {
      selectedStudy.value = await StudyService.getStudy(selectedStudyId.value);
    } else {
      const firstStudyId = studies.value[0].identifier;
      selectedStudy.value = await StudyService.getStudy(firstStudyId);
      selectedStudyId.value = firstStudyId;

      //set selected study in the cache
      LocalStorageUtils.setStudyDetails(selectedStudy.value);
    }
    setStudyDataViewFilter();
   
    studyLoader.value.stopLoadingAndFinish();

    studyPermissions.value = await StudyService.getStudyPermissions(selectedStudy.value!.identifier);
    dashboardStore.setDashboardLinks();
  };

  const getStudy = async (identifier: string) => {
    selectedStudy.value = await StudyService.getStudy(identifier);
    selectedStudyId.value = identifier;

    //set selected study in the cache
    LocalStorageUtils.setStudyDetails(selectedStudy.value);
  };

  const setStudyFromCache = () => {
    const studyId = LocalStorageUtils.getStudyIdentifier();
    if (studyId) {
      selectedStudyId.value = studyId;
    }
  };

  const setStudyDataViewFilter = () => {
    if (!LocalStorageUtils.getCurrentDataViewFilter() && selectedStudy.value) {
      // set initial data view filter, if there doesn't exist one
      if (selectedStudy.value.longitudinal === true) {
        if (selectedStudy.value.flags?.preferences?.dataViewFilter) {
          LocalStorageUtils.setDataViewFilter(selectedStudy.value.flags?.preferences?.dataViewFilter);
        } else {
          LocalStorageUtils.setDataViewFilter(DATA_VIEW_FILTER.CASES);
        }
       
      } else {
        LocalStorageUtils.setDataViewFilter(DATA_VIEW_FILTER.ENTRIES);
      }
    } else if (selectedStudy.value && selectedStudy.value.flags?.preferences?.dataViewFilter) {
      LocalStorageUtils.setDataViewFilter(selectedStudy.value.flags?.preferences?.dataViewFilter);
    }
  };

  const setStudy = async (study: StudyModel) => {
    //unselect selected case
    studyLoader.value.startLoading();
    try {
      entryListStore.selectedCase = undefined;
      selectedStudy.value = await StudyService.getStudy(study.identifier);
      LocalStorageUtils.setStudyDetails(selectedStudy.value);
      selectedStudyId.value = study.identifier;

      studyPermissions.value = await StudyService.getStudyPermissions(selectedStudy.value!.identifier);
      dashboardStore.setDashboardLinks();

      await formStore.getStudyForms();
    } catch (error) {
      studyLoader.value.setError(error);
    } finally {
      studyLoader.value.stopLoadingAndFinish();
    }
  };

  const studyImportDiff = ref<StudyImportDiff>();
  const importExportLoader = ref(new LoadingStateModel({ isLoading: false }));
  const importAndValidateStudy = async (file: File) => {
    importExportLoader.value.startLoading();
    try {
      studyImportDiff.value = await StudyService.importAndValidate(
        selectedStudy.value!.identifier,
        file,
        selectedStudy.value?.flags?.customDataDictionary
      );
    } catch (error) {
      importExportLoader.value.setError(error);
    } finally {
      importExportLoader.value.stopLoadingAndFinish();
    }
  };

  const getExport = async (payload: StudySchemaExport) => {
    importExportLoader.value.startLoading();
    try {
      await StudyService.getStudyExport(selectedStudy.value!.identifier, payload);
    } catch (error) {
      importExportLoader.value.setError(error);
    } finally {
      importExportLoader.value.stopLoadingAndFinish();
    }
  };

  const importAndApproveStudy = async () => {
    importExportLoader.value.startLoading();
    try {
      await StudyService.importStudy(selectedStudy.value!.identifier, studyImportDiff.value!);
      importExportLoader.value.setSuccess('Study updated successfully');
      formStore.getStudyForms();
    } catch (error) {
      importExportLoader.value.setError(error);
    } finally {
      importExportLoader.value.stopLoadingAndFinish();
    }
  };
  
  const createStudy = async (newStudy: Omit<INewStudy, 'organizationIdentifier'>, redirect: boolean = true) => {
    studyLoader.value.startLoading();
    try {
      const { data } = await StudyService.postStudy({
        ...newStudy,
        organizationIdentifier: selectedStudy.value?.organizationIdentifier
      });
      await getStudies();
      await setStudy({ ...data, identifier: data.hexIdentifier });
      pipelineStore.resetStore();
      if (redirect) {
        await router.push({ name: routeConstants.FORMS.name });
      }
    } catch (error) {
      studyLoader.value.setError(error);
    } finally {
      studyLoader.value.stopLoadingAndFinish();
    }
  };

  const importStudyForm = async (fileName: string, filepath: string) => {
    studyLoader.value.startLoading();
    try {
      await StudyService.importStudyForm(selectedStudy.value!.identifier, fileName, filepath);
    } catch (error) {
      studyLoader.value.setError(error);
    } finally {
      studyLoader.value.stopLoadingAndFinish();
    }
  };

  return {
    studies,
    selectedStudy,
    selectedStudyId,
    studyPermissions,
    studyLoader,
    importExportLoader,
    studyImportDiff,
    getStudies,
    getStudy,
    setStudyFromCache,
    setStudy,
    createStudy,
    setStudyDataViewFilter,
    importAndValidateStudy,
    importAndApproveStudy,
    getExport,
    importStudyForm
  };
});
